import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Power, X, Building2, Shield, MapPin, Search, Landmark, IndianRupee, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { errorMessage, formatINR } from '@/lib/utils';
import { LocationPicker } from '@/components/LocationPicker';

// Razorpay sub-merchant statuses that can take payments (mirrors
// app.billing.LIVE_ACCOUNT_STATUSES on the backend).
const LIVE_STATUSES = ['activated', 'instantly_activated', 'activated_kyc_pending'];

// Super-admin-only: create/list/edit/deactivate venues (called "academies"
// on the backend — see README for why that name stayed internal). This is
// the platform-owner side of "super admin creates venue admins; venue
// admins manage their own venue" — the venue has to exist before a venue
// admin can be assigned to it (see AdminUsers.jsx for that step).
//
// PHASE 2 (location/discovery): latitude/longitude are how a venue gets
// geo-tagged for the customer-facing "nearby venues" sort — see
// pages/customer/Venues.jsx and GET /academies/nearby. A venue left
// without coordinates still shows up everywhere (just un-sorted by
// distance), so filling these in is optional, not required.
//
// Address-picker change: address/city/lat/lng are now filled in one step
// via LocationPicker (Google Places search + map pin) instead of typing an
// address and two raw coordinate numbers separately. The underlying fields
// are unchanged — this only changes how the admin fills them in.
const empty = {
  name: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  contact_email: '',
  contact_phone: '',
  logo_url: '',
  latitude: '',
  longitude: '',
  place_id: '',
  // Platform fees (per booking / per plan) are NOT part of this form — they
  // have their own super-admin-only dialog ("Platform Fees" below), since
  // the venue's own admin can edit its plans.
  active: true,
};

export default function AdminVenues() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/academies');
      setRows(data);
    } catch (err) {
      toast({ title: 'Failed to load venues', description: err.response?.data?.detail || '', variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (v) => {
    setForm({
      ...empty,
      ...v,
      latitude: v.latitude ?? '',
      longitude: v.longitude ?? '',
      place_id: v.place_id ?? '',
    });
    setEditing(v.id);
    setOpen(true);
  };

  // Address/location is filled in one step via LocationPicker (search or
  // "use my current location" + map pin) rather than typing address and
  // coordinates separately. Not required — a venue left without one just
  // doesn't get distance-sorted for customers.
  const [pickerOpen, setPickerOpen] = useState(false);
  const applyPickedLocation = ({ address, city, state, pincode, latitude, longitude, place_id }) => {
    setForm((f) => ({
      ...f,
      address: address || f.address,
      city: city || f.city,
      state: state || f.state,
      pincode: /^[0-9]{6}$/.test(pincode || '') ? pincode : f.pincode,
      latitude: latitude.toFixed(6),
      longitude: longitude.toFixed(6),
      place_id: place_id || '',
    }));
    setPickerOpen(false);
  };

  const save = async () => {
    if (form.pincode && !/^[0-9]{6}$/.test(form.pincode)) {
      toast({ title: 'Invalid PIN code', description: 'Enter a 6-digit PIN code or leave it blank.', variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const { name, slug, address, city, state, pincode, contact_email, contact_phone, logo_url, place_id, active } = form;
      const payload = {
        name, slug, address, city, state, pincode, contact_email, contact_phone, logo_url, place_id, active,
        latitude: form.latitude === '' ? null : Number(form.latitude),
        longitude: form.longitude === '' ? null : Number(form.longitude),
      };
      if (editing) await api.put(`/admin/academies/${editing}`, payload);
      else await api.post('/admin/academies', payload);
      toast({ title: editing ? 'Venue updated' : 'Venue created', variant: 'success' });
      setOpen(false);
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || '', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (v) => {
    try {
      await api.put(`/admin/academies/${v.id}/toggle`);
      toast({ title: v.active ? 'Venue deactivated' : 'Venue activated', variant: 'success' });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || '', variant: 'error' });
    }
  };

  // Payments: each venue is a Razorpay sub-merchant under the platform's
  // partner account (Option A onboarding). Add the venue in the Razorpay
  // Partner Dashboard — Razorpay emails it a sign-up/KYC link — then paste
  // the resulting acc_… id here. Customer payments then settle straight to
  // the venue; the platform bills its own fees monthly (Billing pages).
  const [payoutTarget, setPayoutTarget] = useState(null); // venue row, or null
  const [accountIdInput, setAccountIdInput] = useState('');
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [payoutSaving, setPayoutSaving] = useState(false);

  const openPayout = async (v) => {
    setPayoutTarget(v);
    setAccountIdInput('');
    setPayoutStatus(null);
    try {
      const { data } = await api.get(`/admin/academies/${v.id}/razorpay-account`);
      setPayoutStatus(data);
    } catch { /* status refresh is best-effort */ }
  };

  const linkAccount = async () => {
    setPayoutSaving(true);
    try {
      const { data } = await api.post(`/admin/academies/${payoutTarget.id}/razorpay-account`, { account_id: accountIdInput.trim() });
      toast({ title: 'Razorpay account linked', description: `Status: ${data.status}`, variant: 'success' });
      setPayoutTarget(null);
      load();
    } catch (err) {
      toast({ title: 'Linking failed', description: errorMessage(err.response?.data?.detail), variant: 'error' });
    } finally {
      setPayoutSaving(false);
    }
  };

  const unlinkAccount = async () => {
    setPayoutSaving(true);
    try {
      await api.delete(`/admin/academies/${payoutTarget.id}/razorpay-account`);
      toast({ title: 'Razorpay account unlinked', variant: 'success' });
      setPayoutTarget(null);
      load();
    } catch (err) {
      toast({ title: 'Unlink failed', description: errorMessage(err.response?.data?.detail), variant: 'error' });
    } finally {
      setPayoutSaving(false);
    }
  };

  // Platform fees: flat ₹ per online court booking (venue-wide) and per
  // membership purchase (per plan). Billed to the venue monthly, never
  // added to what customers pay.
  const [feesTarget, setFeesTarget] = useState(null);
  const [fees, setFees] = useState({ booking_fee: '', plan_fees: [] });
  const [feesSaving, setFeesSaving] = useState(false);

  const openFees = async (v) => {
    setFeesTarget(v);
    setFees({ booking_fee: '', plan_fees: [] });
    try {
      const { data } = await api.get(`/admin/academies/${v.id}/platform-fees`);
      setFees({
        booking_fee: data.booking_fee || '',
        plan_fees: data.plan_fees.map((p) => ({ ...p, platform_fee: p.platform_fee ?? '' })),
      });
    } catch (err) {
      toast({ title: 'Failed to load fees', description: errorMessage(err.response?.data?.detail), variant: 'error' });
    }
  };

  const saveFees = async () => {
    setFeesSaving(true);
    try {
      const plan_fees = Object.fromEntries(fees.plan_fees.map((p) => [p.id, Number(p.platform_fee) || 0]));
      await api.put(`/admin/academies/${feesTarget.id}/platform-fees`, { booking_fee: Number(fees.booking_fee) || 0, plan_fees });
      toast({ title: 'Platform fees saved', variant: 'success' });
      setFeesTarget(null);
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: errorMessage(err.response?.data?.detail), variant: 'error' });
    } finally {
      setFeesSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-venues-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Platform</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Venues</h1>
        </div>
        <Button onClick={openNew} data-testid="add-venue-button"><Plus size={14} /> Add Venue</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="text-white/40 text-sm col-span-full text-center py-12">Loading venues...</div>
        ) : rows.length === 0 ? (
          <div className="text-white/40 text-sm col-span-full text-center py-12">No venues yet — add the first one.</div>
        ) : rows.map((v) => (
          <div key={v.id} className="bg-surface border border-white/10 p-6 flex flex-col gap-3">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-full bg-volt/10 flex items-center justify-center">
                <Building2 size={18} className="text-volt" />
              </div>
              <Badge tone={v.active ? 'success' : 'default'}>{v.active ? 'Active' : 'Inactive'}</Badge>
            </div>
            <div>
              <div className="font-heading font-bold text-xl uppercase">{v.name}</div>
              <div className="text-xs text-white/40 mt-1">/{v.slug}</div>
              {(v.address || v.city) && (
                <div className="text-sm text-white/50 mt-2">{[v.address, v.city, v.state, v.pincode].filter(Boolean).join(', ')}</div>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-white/50 mt-1">
              <span className="flex items-center gap-1"><Shield size={12} /> {v.admin_count ?? 0} staff</span>
              <span className="flex items-center gap-1"><Building2 size={12} /> {v.court_count ?? 0} courts</span>
              {v.latitude != null && v.longitude != null ? (
                <span className="flex items-center gap-1 text-volt" title="Appears in nearby-venue sorting">
                  <MapPin size={12} /> Geo-tagged
                </span>
              ) : null}
              {v.booking_fee ? (
                <span title="Platform fee per online booking, billed monthly">{formatINR(v.booking_fee)}/booking</span>
              ) : null}
            </div>
            <div className="text-xs">
              {v.razorpay_account_id ? (
                <Badge tone={LIVE_STATUSES.includes(v.razorpay_account_status) ? 'success' : 'warning'}>
                  <Landmark size={10} className="inline mr-1" />
                  Razorpay {LIVE_STATUSES.includes(v.razorpay_account_status) ? 'live' : (v.razorpay_account_status || 'created').replace(/_/g, ' ')}
                </Badge>
              ) : (
                <span className="text-white/40">No Razorpay account linked</span>
              )}
              {v.billing_paused ? <Badge tone="danger" className="ml-2">Billing paused</Badge> : null}
            </div>
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={() => openEdit(v)} className="flex-1"><Edit3 size={12} /> Edit</Button>
              <Button variant="outline" size="sm" onClick={() => toggle(v)} className="flex-1">
                <Power size={12} /> {v.active ? 'Deactivate' : 'Activate'}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openPayout(v)} className="flex-1" data-testid={`payout-${v.id}`}>
                <Landmark size={12} /> {v.razorpay_account_id ? 'Razorpay Account' : 'Link Razorpay'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => openFees(v)} className="flex-1" data-testid={`fees-${v.id}`}>
                <IndianRupee size={12} /> Platform Fees
              </Button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-surface border border-white/10 max-w-lg w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="venue-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl uppercase">{editing ? 'Edit' : 'New'} Venue</h2>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Kalgyan Badminton Academy" data-testid="venue-name" /></Field>
              {/* Address is typed by hand; finding it on Google Maps is an optional
                  shortcut that fills these fields and the coordinates used for
                  "nearby venues". Some venues aren't on Maps at all. */}
              <div className="border border-white/10 p-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">Address</div>
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-volt hover:underline"
                    data-testid="venue-address-picker-trigger"
                  >
                    <Search size={12} /> Find on Google Maps
                  </button>
                </div>
                <Field label="Address line">
                  <Textarea
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Building, street, area, landmark"
                    className="min-h-[72px]"
                    data-testid="venue-address"
                  />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="venue-city" /></Field>
                  <Field label="State"><Input value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Karnataka" data-testid="venue-state" /></Field>
                  <Field label="PIN code">
                    <Input
                      value={form.pincode || ''}
                      inputMode="numeric"
                      maxLength={6}
                      onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/[^0-9]/g, '').slice(0, 6) })}
                      placeholder="560001"
                      data-testid="venue-pincode"
                    />
                  </Field>
                </div>
                {form.latitude && form.longitude ? (
                  <div className="flex items-center justify-between gap-3 text-[11px]">
                    <span className="flex items-center gap-1 text-volt">
                      <MapPin size={11} /> On the map ({Number(form.latitude).toFixed(4)}, {Number(form.longitude).toFixed(4)})
                    </span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, latitude: '', longitude: '', place_id: '' })}
                      className="text-white/40 hover:text-white"
                    >
                      Remove pin
                    </button>
                  </div>
                ) : (
                  <div className="text-[11px] text-white/40">
                    Not on the map — customers can still find this venue in the full list, but it won't be sorted by distance.
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Contact Email"><Input type="email" value={form.contact_email || ''} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
                <Field label="Contact Phone"><Input value={form.contact_phone || ''} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></Field>
              </div>
              <Field label="Logo URL (optional)"><Input value={form.logo_url || ''} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…" /></Field>
              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-volt" />
                Active (visible to customers)
              </label>
            </div>
            <div className="flex gap-3 mt-8">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={save} disabled={saving} className="flex-1" data-testid="save-venue">{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        </div>
      )}

      {payoutTarget && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPayoutTarget(null)}>
          <div className="bg-surface border border-white/10 max-w-lg w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="payout-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl uppercase">Razorpay — {payoutTarget.name}</h2>
              <button onClick={() => setPayoutTarget(null)} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>

            {payoutTarget.razorpay_account_id ? (
              <div className="space-y-4">
                <div className="bg-obsidian border border-white/10 p-5 space-y-2 text-sm">
                  <div><span className="text-white/40 uppercase text-[10px] tracking-[0.25em] mr-2">Status</span>
                    <Badge tone={LIVE_STATUSES.includes(payoutStatus?.status || payoutTarget.razorpay_account_status) ? 'success' : 'warning'}>
                      {(payoutStatus?.status || payoutTarget.razorpay_account_status || 'created').replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <div><span className="text-white/40 uppercase text-[10px] tracking-[0.25em] mr-2">Account ID</span><span className="font-mono text-xs">{payoutTarget.razorpay_account_id}</span></div>
                </div>
                {!LIVE_STATUSES.includes(payoutStatus?.status || payoutTarget.razorpay_account_status) && (
                  <p className="text-xs text-white/50">
                    Not live yet — the venue still needs to finish Razorpay's sign-up/KYC (the link Razorpay emailed them), or Razorpay is reviewing it.
                    Online bookings for this venue stay off until Razorpay activates the account.
                  </p>
                )}
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => openPayout(payoutTarget)} className="flex-1">Refresh Status</Button>
                  <Button variant="outline" onClick={unlinkAccount} disabled={payoutSaving} className="flex-1" data-testid="unlink-payout">Unlink</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <ol className="text-xs text-white/60 space-y-2 list-decimal pl-4 -mt-2">
                  <li>
                    In the Razorpay Partner Dashboard, add <b>{payoutTarget.name}</b> as a sub-merchant
                    {payoutTarget.contact_email ? <> (email: <span className="font-mono">{payoutTarget.contact_email}</span>)</> : null}.
                    {' '}<a href="https://dashboard.razorpay.com/" target="_blank" rel="noreferrer" className="text-volt inline-flex items-center gap-1">Open Razorpay <ExternalLink size={10} /></a>
                  </li>
                  <li>Razorpay emails the venue a sign-up link to complete KYC and add its bank account.</li>
                  <li>Paste the venue's account ID (starts with <span className="font-mono">acc_</span>) below.</li>
                </ol>
                {payoutStatus?.legacy_route_account_id && (
                  <p className="text-xs text-amber-300/80">
                    This venue had an old Route split-payment account ({payoutStatus.legacy_route_account_id}). That model isn't used any more — link its sub-merchant account instead.
                  </p>
                )}
                <Field label="Razorpay account ID">
                  <Input value={accountIdInput} onChange={(e) => setAccountIdInput(e.target.value)} placeholder="acc_XXXXXXXXXXXXXX" className="font-mono" data-testid="payout-account-id" />
                </Field>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setPayoutTarget(null)} className="flex-1">Cancel</Button>
                  <Button onClick={linkAccount} disabled={payoutSaving || !accountIdInput.trim()} className="flex-1" data-testid="save-payout">{payoutSaving ? 'Linking…' : 'Link Account'}</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {feesTarget && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setFeesTarget(null)}>
          <div className="bg-surface border border-white/10 max-w-lg w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="fees-modal">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading font-bold text-2xl uppercase">Platform Fees — {feesTarget.name}</h2>
              <button onClick={() => setFeesTarget(null)} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>
            <p className="text-xs text-white/50 mb-6">
              Flat amounts this venue pays you per successful online payment, invoiced monthly. Customers' prices don't change.
            </p>
            <div className="space-y-4">
              <Field label="Per court booking (₹)">
                <Input type="number" min="0" step="1" value={fees.booking_fee} onChange={(e) => setFees({ ...fees, booking_fee: e.target.value })} placeholder="0" data-testid="fees-booking" />
              </Field>
              <div className="border-t border-white/10 pt-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Per membership purchase</div>
                {fees.plan_fees.length === 0 ? (
                  <div className="text-xs text-white/40">This venue has no membership plans yet.</div>
                ) : fees.plan_fees.map((p, i) => (
                  <div key={p.id} className="flex items-center gap-3 mb-3">
                    <div className="flex-1 text-sm">
                      {p.name} <span className="text-white/40 text-xs">· {formatINR(p.price)}{p.active === false ? ' · inactive' : ''}</span>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      className="w-28"
                      value={p.platform_fee}
                      onChange={(e) => setFees({ ...fees, plan_fees: fees.plan_fees.map((x, idx) => (idx === i ? { ...x, platform_fee: e.target.value } : x)) })}
                      placeholder="0"
                      data-testid={`fees-plan-${p.id}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setFeesTarget(null)} className="flex-1">Cancel</Button>
              <Button onClick={saveFees} disabled={feesSaving} className="flex-1" data-testid="save-fees">{feesSaving ? 'Saving…' : 'Save Fees'}</Button>
            </div>
          </div>
        </div>
      )}

      <LocationPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={applyPickedLocation}
        title="Search for the venue's address"
      />
    </div>
  );
}
